import { Provider } from '@vgtv/api-client';
import { formatPublishedDate, formatUpcomingDate } from '@vgtv/time-helpers';
import { ComponentType, DataType } from '@vgtv/api-client/lib/component';

import { config } from '../../config.js';
import { getAssetProps } from '../../helpers/getAssetProps.js';
import { ABCategory, VGTVCategory } from '../../types.js';
import { isTruthy } from '../../utils/types.js';
import {
  getAssetAccessLevel,
  isAssetPaywalled,
  getAssetFlightTimesStart,
  isAssetUpcoming,
  isDocumentary,
  willAssetHaveDisplayAd,
  normalizeAspectRatio,
  getAssetShortPath,
  getAssetMetadata,
} from '../../player/playerAssetHelpers.js';
import { STORIES_TAG_ID } from '../../consts.js';
import { isMobile } from '../../utils/device.js';

import {
  getCategoryAgeLimit,
  getCategoryMetadata,
  getCategoryPromoUrl,
  isCategoryRestricted,
} from './categoryHelpers.js';
import { getPath, pathToUrl, routeNames } from './routes.js';
import {
  type AssetContextSource,
  getAssetContextFromComponent,
} from './componentHelpers.js';

import type { Vendor } from '../../types.js';
import type { AssetMetadata } from '@schibsted-svp/svp-api-types';
import type { CnpStory } from '@vgtv/api-client/lib/cnp_story';
import type { CnpTag } from '@vgtv/api-client/lib/cnp_tag';
import type { IMBOImageUrl, UnixTimestamp } from '@vgtv/api-client';
import type {
  SvpAsset,
  SvpAssetAdditional,
} from '@vgtv/api-client/lib/svp_asset';

const LOGIN_ACCESS_LEVEL =
  config.provider === Provider.VGTV ? 'premium' : 'login';

export type SvpAssetWithEmbedded = SvpAssetAdditional & {
  _embedded: {
    tags: { id: string; tag: string }[];
  };
};

type AssetPreviewType = 'promo' | 'portrait';
type AssetPreview = {
  aspectRatio: `${number}`;
  width: number;
  height: number;
  url: string;
};

const getAssetPreviews = (
  asset: SvpAssetAdditional,
  previewName: AssetPreviewType
) => {
  const str = getAssetMetadata(asset)[`preview_${previewName}`];
  try {
    return JSON.parse(str) as AssetPreview[][];
  } catch {
    return null;
  }
};

/**
 * Returns a numerical aspect ratio value.
 */
function getAssetAspectRatio(asset?: {
  additional?: { metadata?: AssetMetadata };
}): number {
  const metadata = getAssetMetadata(asset);

  const aspectRatio = parseFloat(metadata.aspectRatio);

  return normalizeAspectRatio(aspectRatio);
}

export interface SvpTag {
  id: CnpTag['id'];
  tag: string | null;
}

const emptyChaptersArray = Object.freeze([] as SvpTag[]);
function getAssetChapters(asset: SvpAssetAdditional) {
  return asset.additional.chapters ?? emptyChaptersArray;
}

const emptyTagsArray = Object.freeze([] as SvpTag[]);
function getAssetTags(asset: SvpAssetAdditional) {
  const assetTagIds: string[] = asset.additional.tags ?? [];
  const embeddedTags =
    ((asset as SvpAssetWithEmbedded)._embedded?.tags as SvpTag[]) ?? [];

  if (assetTagIds.length > 0 && embeddedTags.length > 0) {
    return assetTagIds
      .map((tagId) => embeddedTags.find(({ id }) => tagId === id))
      .filter(isTruthy);
  }

  return emptyTagsArray;
}

const getAssetAgeLimit = (asset: SvpAssetAdditional) => {
  const ageLimit = asset.additional.settings.ageLimit;
  if (ageLimit !== undefined && ageLimit !== null) {
    return ageLimit;
  }

  const categoryAgeLimit = getCategoryAgeLimit(asset.category);
  if (asset.series && categoryAgeLimit !== undefined) {
    return categoryAgeLimit;
  }

  return undefined;
};

function getAssetMainImage(asset: SvpAsset): IMBOImageUrl {
  return asset.images.main;
}

export const getAssetPreviewUrl = (asset: SvpAssetAdditional) => {
  const metadata = getAssetMetadata(asset);

  return metadata.preview_vivi_category ?? metadata.preview;
};

function getAssetVerticalImage(
  asset: SvpAssetAdditional
): IMBOImageUrl | undefined {
  return (
    getAssetMetadata(asset).verticalPosterImage || getAssetMainImage(asset)
  );
}

export type SvpEpisode = Omit<SvpAssetAdditional, 'series'> & {
  series: NonNullable<SvpAssetAdditional['series']>;
};

function isTVShowEpisode(asset: SvpAssetAdditional): asset is SvpEpisode {
  return Boolean(asset.series && asset.category.isSeries);
}

/**
 * Returns true if asset belongs to a category that is entertainment
 * or it's parent category is entertainment
 */
function isEntertainment(asset: SvpAsset): boolean {
  const { category, provider } = asset;

  switch (provider) {
    case 'vgtv':
      return (
        category.id === VGTVCategory.ENTERTAINMENT ||
        category.parentId === VGTVCategory.ENTERTAINMENT
      );
    case 'ab':
      return (
        category.id === ABCategory.ENTERTAINMENT ||
        category.parentId === ABCategory.ENTERTAINMENT
      );
  }
  return false;
}

/**
 * Returns true if asset is login secured only.
 */
function isAssetLoginPaywalled(asset: {
  additional?: {
    access?: SvpAssetAdditional['additional']['access'];
  };
}): boolean {
  return getAssetAccessLevel(asset) === LOGIN_ACCESS_LEVEL;
}

/**
 * Returns true if an asset has `streamType` set to `live`.
 * NOTE: this doesn't necessarily mean that the asset is currently live.
 */
function isLiveStream(asset: SvpAsset): boolean {
  return asset.streamType === 'live';
}

/**
 * Returns the end flight time for assets which should be available for a limited time.
 */
function getAssetFlightTimesEnd(
  asset: SvpAsset
): UnixTimestamp | undefined | null {
  const { flightTimes } = asset;

  return flightTimes?.end;
}

function isAssetExpired(asset: SvpAsset, relativeTo?: UnixTimestamp): boolean {
  const end = getAssetFlightTimesEnd(asset);

  if (!end) {
    return false;
  }

  const nowUnixTimestamp = relativeTo || Date.now() / 1000;

  return nowUnixTimestamp >= end;
}

export const getUpcomingAssetFlightTimesStart = (asset: SvpAssetAdditional) => {
  if (isLiveStream(asset)) {
    return getLiveAssetFlightTimesStart(asset);
  }

  return getAssetFlightTimesStart(asset);
};

function getLiveAssetFlightTimesStart(
  asset: SvpAssetAdditional
): number | undefined {
  if (!isLiveStream(asset)) {
    return undefined;
  }

  const {
    flightTimes,
    additional: { metadata: { eventStartTime, studioStartTime } = {} } = {},
  } = asset;

  if (studioStartTime) {
    return parseInt(studioStartTime, 10);
  }

  if (eventStartTime) {
    return parseInt(eventStartTime, 10);
  }

  return flightTimes?.start || undefined;
}

/**
 * Returns true if an asset is currently live, based on its `streamType` being
 * `live` and `flightTimes.start` being in the past.
 */
function isCurrentlyLive(
  asset: SvpAssetAdditional,
  relativeTo?: UnixTimestamp
): boolean {
  const start = getLiveAssetFlightTimesStart(asset);

  if (!start) {
    return false;
  }

  const nowUnixTimestamp = relativeTo || Date.now() / 1000;

  return nowUnixTimestamp >= start;
}

/**
 * Returns true if an asset will be live in the future, based on its `streamType` being
 * `live` and `flightTimes.start` being in the future.
 */
function isUpcomingLive(
  asset: SvpAssetAdditional,
  relativeTo?: UnixTimestamp
): boolean {
  const start = getLiveAssetFlightTimesStart(asset);

  if (!start) {
    return false;
  }

  const nowUnixTimestamp = relativeTo || Date.now() / 1000;

  return nowUnixTimestamp < start;
}

/**
 * Returns the collection of assets sorted by flightTimes.start ascending, e.g.
 * assets which will start sooner are at the beginning of the list.
 */
function sortAssetsByFlightTimes<T extends SvpAsset>(
  assets: T[],
  desc: boolean = false
): T[] {
  const unit = desc ? -1 : 1;
  return [...assets].sort((a, b) => {
    const startA = getAssetFlightTimesStart(a) as number;
    const startB = getAssetFlightTimesStart(b) as number;

    return startA < startB ? -unit : unit;
  });
}

function isAssetRestricted(vendor: Vendor, asset: SvpAsset): boolean {
  return asset.category ? isCategoryRestricted(vendor, asset.category) : false;
}

function getAssetDisplayTitle(asset: SvpAsset): string {
  return asset.title
    .replace(/^DIREKTE: /, '')
    .replace(/^DIREKTE ([0-9]{2}\.[0-9]{2}): /, '')
    .replace(/\([0-9]{2}[.:][0-9]{2}\)$/, '');
}

function isAssetActive(asset: SvpAsset): boolean {
  return asset.status === 'active';
}

/**
 * Returns true for all audio assets (regardless of the podcast flag).
 */
function isAudioAsset(asset: SvpAsset): boolean {
  return asset.assetType === 'audio';
}

/**
 * Determines whether an asset should be allowed to be open / played. Use to render 404 instead.
 */
function isPlaybackValidAsset(vendor: Vendor, asset: SvpAsset): boolean {
  return (
    isAssetActive(asset) &&
    !isAssetRestricted(vendor, asset) &&
    !isAudioAsset(asset)
  );
}

function getAssetUrl(
  provider: Provider,
  asset: SvpAssetAdditional,
  start?: number
): string {
  const baseUrl = getBaseUrl(provider);
  const pagePath = getPath(routeNames.asset, {
    asset,
    start,
    skipDeeplinkCheck: true,
  });

  return `${baseUrl}${pagePath}`;
}

function getBaseUrl(provider: Provider): string {
  switch (provider) {
    case Provider.VGTV:
      return 'https://tv.vg.no';
    case Provider.AB:
      return 'https://tv.aftonbladet.se';
  }
}

function getStudioStartTime(
  asset: SvpAssetAdditional
): UnixTimestamp | undefined {
  const { additional: { metadata: { studioStartTime } = {} } = {} } = asset;

  return studioStartTime ? parseInt(studioStartTime, 10) : undefined;
}

function getEventStartTime(
  asset: SvpAssetAdditional
): UnixTimestamp | undefined {
  const { additional: { metadata: { eventStartTime } = {} } = {} } = asset;

  return eventStartTime ? parseInt(eventStartTime, 10) : undefined;
}

function assetHasTag(asset: SvpAssetAdditional, tagIds?: CnpTag['id'][]) {
  return asset.additional?.tags?.some((assetTag) => tagIds?.includes(assetTag));
}

function assetHasStory(asset: SvpAssetAdditional, storyId?: CnpStory['id']) {
  return asset.additional?.stories?.includes(storyId!);
}

export const formatAssetUpcomingDate = (asset: SvpAssetAdditional) => {
  return formatUpcomingDate({
    timestamp: getUpcomingAssetFlightTimesStart(asset)!,
    locale: config.provider === Provider.VGTV ? 'nb' : 'sv',
    streamType: asset.streamType,
  });
};

export const isAssetSponsored = (asset: SvpAssetAdditional) => {
  const assetMetadata = getAssetMetadata(asset);
  const categoryMetadata = getCategoryMetadata(asset.category);
  const CAMPAIGN_CATEGORY_ID = 214;
  const { sponsoredLinkUrl, sponsoredLinkText } = assetMetadata ?? {};

  const isSponsoredVGTV =
    assetMetadata?.sponsored === 'true' ||
    categoryMetadata?.sponsored?.value === 'true';

  const isSponsored =
    config.provider === Provider.VGTV
      ? isSponsoredVGTV
      : isSponsoredVGTV && asset.category.parentId === CAMPAIGN_CATEGORY_ID;

  const hasSponsoredLink = sponsoredLinkText && sponsoredLinkUrl;

  return Boolean(isSponsored && hasSponsoredLink);
};

const getAssetLabels = (
  asset: SvpAssetAdditional,
  component?: AssetContextSource,
  isNextAsset = false
): string[] => {
  const { category } = asset;
  const { seasonNumber, episodeNumber } = asset.series ?? {};

  const flags = {
    category: true,
    next: isNextAsset,
    episode: false,
    series: false,
    eventStart: false,
    publication: false,
  };

  const context = getAssetContextFromComponent(component);

  if (
    flags.next ||
    category.id === context?.categoryId ||
    assetHasTag(asset, context?.tagId) ||
    assetHasStory(asset, context?.storyId)
  ) {
    flags.category = false;
  }

  if (isAssetSponsored(asset)) {
    flags.category = true;
  }

  if (category.isSeries && asset.series) {
    flags.episode = true;
    if (
      context?.componentType !== ComponentType.SLIDER ||
      context.dataType !== DataType.SEASONS
    ) {
      flags.series = true;
    }
  }

  if (isUpcomingLive(asset)) {
    flags.eventStart = true;
  }

  if (
    context &&
    !isDocumentary(asset) &&
    !isEntertainment(asset) &&
    Object.values(flags).filter(Boolean).length < 2
  ) {
    flags.publication = true;
  }

  const labels = [];

  if (flags.category) {
    labels.push(category.title);
  }

  if (flags.next) {
    labels.push(config.provider === Provider.VGTV ? 'neste' : 'nästa');
  }

  if (flags.series) {
    if (flags.category || flags.next) {
      // shorthand
      labels.push(`S${seasonNumber}, E${episodeNumber}`);
    } else {
      labels.push(
        config.provider === Provider.VGTV
          ? `Sesong ${seasonNumber}, Episode ${episodeNumber}`
          : `Säsong ${seasonNumber}, Avsnitt ${episodeNumber}`
      );
    }
  } else if (flags.episode) {
    labels.push(
      config.provider === Provider.VGTV
        ? `Episode ${episodeNumber}`
        : `Avsnitt ${episodeNumber}`
    );
  }

  if (flags.eventStart) {
    labels.push(formatAssetUpcomingDate(asset));
  }

  if (flags.publication) {
    const { publishedDate } = getAssetProps(asset);
    if (publishedDate) {
      labels.push(
        formatPublishedDate(
          publishedDate,
          config.provider === Provider.VGTV ? 'nb' : 'sv'
        )
      );
    }
  }

  return labels;
};

function getAssetTeaserDescription(asset: SvpAsset) {
  const { category } = asset;

  if (isDocumentary(asset) || category.isSeries) return asset.description;
  return undefined;
}

const subscriptionNameMappings = {
  [Provider.VGTV]: {
    plus: 'VG+',
    sport: 'VG+ Sport',
  },
  [Provider.AB]: {
    plus: 'Plus',
  },
} as const;

function getSubscriptionLabel(asset: SvpAsset): string | undefined {
  const access = getAssetAccessLevel(asset);
  const mapping = subscriptionNameMappings[config.provider];
  // @ts-expect-error undefined is fine
  return mapping[access];
}

function getAssetPromoUrl(asset: SvpAssetAdditional) {
  const preview = getAssetPreviews(asset, 'promo')?.[0];
  if (preview && preview.length > 0) {
    const sorted = [...preview].sort((a, b) => a.width - b.width);
    return (isMobile ? sorted.shift() : sorted.pop())!.url;
  }

  return (
    getAssetMetadata(asset).promoUrl || getCategoryPromoUrl(asset.category)
  );
}

function getEmbeddedTags(asset: SvpAssetWithEmbedded) {
  return asset._embedded?.tags ?? [];
}

export const getAssetByline = (asset: SvpAssetAdditional) => {
  const { bylineName, bylineEmail } = getAssetMetadata(asset);

  if (bylineName && bylineEmail) {
    return {
      name: bylineName.trim(),
      email: bylineEmail.trim(),
    };
  }

  return undefined;
};

const getAuthors = (asset: SvpAssetAdditional) => {
  return getAssetMetadata(asset).authors;
};

export const getSeoAssetTitle = (asset: SvpAssetAdditional): string => {
  return (getAssetMetadata(asset).seoTitle || '').trim() || asset.title;
};

export const getSeoAssetDescription = (asset: SvpAssetAdditional): string => {
  return (
    (getAssetMetadata(asset).seoDescription || '').trim() ||
    asset.description ||
    ''
  );
};

export const getAssetShareUrl = (asset: SvpAssetAdditional) => {
  return pathToUrl(
    getPath(routeNames.asset, {
      asset,
      skipDeeplinkCheck: true,
    })
  );
};

const FOURTEEN_DAYS_IN_SECONDS = 14 * 24 * 60 * 60;

export const isNewlyReleased = (
  asset: SvpAssetAdditional,
  relativeTo?: number
) => {
  if (isAssetUpcoming(asset, relativeTo)) {
    return false;
  }

  const flightTimesStart = getAssetFlightTimesStart(asset);

  if (!flightTimesStart) {
    return false;
  }

  const nowUnixTimestamp = relativeTo || Date.now() / 1000;

  return flightTimesStart + FOURTEEN_DAYS_IN_SECONDS > nowUnixTimestamp;
};

function isStoryAsset(asset: SvpAssetAdditional) {
  return asset.additional.tags?.includes(STORIES_TAG_ID);
}

function getStoryUrl(asset: SvpAssetAdditional) {
  if (isStoryAsset(asset) && config.provider === Provider.VGTV) {
    const slug = getAssetMetadata(asset).titleShortPath;
    return `https://www.vg.no/stories/${asset.id}${slug ? `/${slug}` : ''}`;
  }
}

export {
  getAssetPromoUrl,
  getAssetAspectRatio,
  getAssetChapters,
  getAssetTags,
  isDocumentary,
  getAssetMetadata,
  getAssetMainImage,
  getAssetVerticalImage,
  getAssetAccessLevel,
  getAssetAgeLimit,
  isAssetPaywalled,
  isLiveStream,
  getLiveAssetFlightTimesStart,
  isCurrentlyLive,
  isUpcomingLive,
  sortAssetsByFlightTimes,
  isAssetRestricted,
  getAssetDisplayTitle,
  isAssetActive,
  isAssetLoginPaywalled,
  willAssetHaveDisplayAd,
  getAssetFlightTimesStart,
  isAssetUpcoming,
  isAssetExpired,
  isAudioAsset,
  isPlaybackValidAsset,
  getBaseUrl,
  getAssetShortPath,
  getAssetUrl,
  getStudioStartTime,
  getEventStartTime,
  getAssetLabels,
  getAssetTeaserDescription,
  getSubscriptionLabel,
  getEmbeddedTags,
  isEntertainment,
  isTVShowEpisode,
  isStoryAsset,
  getStoryUrl,
  getAssetPreviews,
  getAuthors,
};
